import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["id"];
import { computed, reactive, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Button, Modal } from "@eudonet/opal";
import AddressFormFields from "./AddressFormFields.vue";
import { carryStreetToLayout, toAddressLayout, requiredAddressFields, } from "@/shared/utils/addressFieldConfig";
import { useWorkspaceCountry } from "@/shared/composables/useWorkspaceCountry";
export default /*@__PURE__*/ _defineComponent({
    __name: 'AddressDetailsModal',
    props: {
        modelValue: { type: Boolean },
        initialDetails: {},
        readonly: { type: Boolean },
        showRequiredErrors: { type: Boolean }
    },
    emits: ["update:modelValue", "save"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const titleId = useId();
        let local = reactive({ ...props.initialDetails });
        const touched = reactive({});
        const formFieldsRef = ref(null);
        const layout = useWorkspaceCountry();
        let lastSynced = { ...local };
        function syncLocalFromProps() {
            Object.assign(local, props.initialDetails);
            carryStreetToLayout(local, layout.value);
            lastSynced = { ...local };
        }
        syncLocalFromProps();
        // True as long as the user hasn't changed anything since the last sync — used to let a
        // late-arriving initialDetails update (e.g. an async parent load that resolves after this modal
        // was already opened) refresh `local` without ever clobbering an in-progress edit.
        function isLocalUnedited() {
            return Object.keys(lastSynced).every((key) => local[key] === lastSynced[key]);
        }
        function resetTouched() {
            for (const key of Object.keys(touched)) {
                delete touched[key];
            }
        }
        function touchRequiredFields() {
            for (const field of requiredFields.value) {
                touched[field.key] = true;
            }
        }
        watch(() => props.modelValue, (val) => {
            if (val) {
                syncLocalFromProps();
                resetTouched();
                if (props.showRequiredErrors)
                    touchRequiredFields();
                formFieldsRef.value?.clearSuggestions();
            }
        });
        // CONSTRAINT: initialDetails is a reactive() object the caller never reassigns — only its own
        // fields mutate (e.g. an async entity/address load resolving while this modal is already open) —
        // so this watch needs `deep: true` to see those changes at all.
        watch(() => props.initialDetails, () => {
            if (props.modelValue && isLocalUnedited())
                syncLocalFromProps();
        }, { deep: true });
        const requiredFields = computed(() => requiredAddressFields(layout.value));
        const requiredFilled = computed(() => requiredFields.value.every((field) => local[field.key].trim()));
        const detailsErrors = computed(() => {
            const errors = {};
            for (const field of requiredFields.value) {
                if (touched[field.key] && !local[field.key].trim() && field.requiredMessageKey) {
                    errors[field.key] = t(field.requiredMessageKey);
                }
            }
            return errors;
        });
        function onFieldBlur(key) {
            touched[key] = true;
        }
        function handleSave() {
            touchRequiredFields();
            if (!requiredFilled.value)
                return;
            emit("save", toAddressLayout(local, layout.value));
            emit("update:modelValue", false);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                "model-value": __props.modelValue,
                size: "sm",
                "show-close-button": true,
                "footer-position": "right",
                "aria-labelledby": _unref(titleId),
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (_ctx.$emit('update:modelValue', $event)))
            }, {
                header: _withCtx(() => [
                    _createElementVNode("h2", {
                        id: _unref(titleId),
                        class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                    }, _toDisplayString(_ctx.$t("organisation.detail.mainInformation.addressModalTitle")), 9 /* TEXT, PROPS */, _hoisted_1)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        size: "md",
                        label: _ctx.$t('form.cancel'),
                        onClick: _cache[1] || (_cache[1] = ($event) => (_ctx.$emit('update:modelValue', false)))
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        size: "md",
                        label: _ctx.$t('form.save'),
                        disabled: !requiredFilled.value || __props.readonly,
                        onClick: handleSave
                    }, null, 8 /* PROPS */, ["label", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createVNode(AddressFormFields, {
                        ref_key: "formFieldsRef",
                        ref: formFieldsRef,
                        details: _unref(local),
                        "onUpdate:details": _cache[0] || (_cache[0] = ($event) => (_isRef(local) ? (local).value = $event : local = $event)),
                        errors: detailsErrors.value,
                        readonly: __props.readonly,
                        onBlur: onFieldBlur
                    }, null, 8 /* PROPS */, ["details", "errors", "readonly"])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["model-value", "aria-labelledby"]));
        };
    }
});
